import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormSchemaVersion, rollbackSchema, loadActiveSchema } from '../services/formSchemaService'

// ─── Types ────────────────────────────────────────────────────────────────────

interface FieldInfo   { label: string; key: string; type: string }
interface FieldUpdate { key: string; oldLabel: string; newLabel: string; type: string }
interface DiffResult  { added: FieldInfo[]; removed: FieldInfo[]; updated: FieldUpdate[] }
type FilterChip    = 'all' | 'current' | 'published'
type VersionStatus = 'current' | 'published'

// ─── Helpers (keep as-is per spec) ───────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractFields(schema: any): FieldInfo[] {
  const fields: FieldInfo[] = []
  for (const container of schema?.containersData ?? []) {
    for (const row of container?.rowData ?? []) {
      for (const child of row?.childData ?? []) {
        fields.push({ label: child.label || child.id, key: child.internalName || child.id, type: child.type })
        for (const sub of child?.subChild ?? []) {
          fields.push({ label: sub.label || sub.id, key: sub.internalName || sub.id, type: sub.type })
        }
      }
    }
  }
  return fields
}

function diffVersions(current: FormSchemaVersion, previous?: FormSchemaVersion): DiffResult {
  const curr    = extractFields(JSON.parse(current.Schema))
  const prev    = previous ? extractFields(JSON.parse(previous.Schema)) : []
  const prevMap = new Map(prev.map(f => [f.key, f]))
  const currMap = new Map(curr.map(f => [f.key, f]))
  return {
    added:   curr.filter(f => !prevMap.has(f.key)),
    removed: prev.filter(f => !currMap.has(f.key)),
    updated: curr
      .filter(f => prevMap.has(f.key) && prevMap.get(f.key)!.label !== f.label)
      .map(f  => ({ key: f.key, oldLabel: prevMap.get(f.key)!.label, newLabel: f.label, type: f.type })),
  }
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function getStatus(v: FormSchemaVersion): VersionStatus {
  return v.IsActive ? 'current' : 'published'
}

function getInitials(name: string): string {
  return name.trim().split(/\s+/).map(p => p[0]?.toUpperCase() ?? '').slice(0, 2).join('')
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// ─── Scoped styles ────────────────────────────────────────────────────────────

const VH_STYLES = `
.vh-panel{position:relative;display:flex;flex-direction:column;min-width:640px;max-height:80vh;overflow:hidden;font-size:13px;color:#1A2035}
.vh-header{background:#F7F8FA;border-bottom:1px solid #E4E7EC;padding:14px 18px 0;flex-shrink:0}
.vh-title-row{display:flex;align-items:center;gap:10px;padding-bottom:12px}
.vh-title{font-size:15px;font-weight:500;color:#1A2035;margin:0}
.vh-count{font-size:12px;color:#607D8B;font-weight:400;margin-right:auto}
.vh-close{width:28px;height:28px;background:transparent;border:none;cursor:pointer;color:#607D8B;display:flex;align-items:center;justify-content:center;border-radius:6px;padding:0;flex-shrink:0}
.vh-close:hover{background:#E4E7EC}
.vh-toolbar{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:0 18px 12px}
.vh-search{position:relative;flex:0 1 280px;min-width:200px}
.vh-search input{width:100%;height:30px;padding:0 10px 0 30px;font-size:13px;border:1px solid #E4E7EC;border-radius:6px;background:#FFFFFF;color:#1A2035;box-sizing:border-box}
.vh-search input:focus{outline:none;border-color:#185FA5;box-shadow:0 0 0 2px #E6F1FB}
.vh-search .icon-rb-search{position:absolute;left:10px;top:50%;transform:translateY(-50%);color:#90A4AE;font-size:14px;pointer-events:none}
.vh-filters{display:flex;align-items:center;gap:6px;flex-shrink:0}
.vh-chip{height:30px;padding:0 12px;font-size:12px;font-weight:500;border:1px solid #E4E7EC;border-radius:999px;background:transparent;color:#607D8B;cursor:pointer;display:inline-flex;align-items:center;transition:background 120ms ease,color 120ms ease,border-color 120ms ease}
.vh-chip:hover:not(.is-active){background:#F7F8FA;color:#1A2035}
.vh-chip.is-active{background:#185FA5;border-color:#185FA5;color:#FFFFFF}
.vh-timeline{flex:1;overflow-y:auto;padding:16px 20px 24px;scrollbar-width:thin;scrollbar-color:#C0CAD8 transparent}
.vh-timeline::-webkit-scrollbar{width:8px}
.vh-timeline::-webkit-scrollbar-thumb{background:#C0CAD8;border-radius:4px}
.vh-timeline::-webkit-scrollbar-track{background:transparent}
.vh-rail{position:relative;padding-left:28px}
.vh-rail::before{content:'';position:absolute;left:9px;top:24px;bottom:24px;width:2px;background:#D0D5DD;border-radius:2px;pointer-events:none;z-index:1}
.vh-card{margin-bottom:12px;position:relative}
.vh-dot{position:absolute;left:-23px;top:18px;width:12px;height:12px;border-radius:50%;border:2px solid #fff;z-index:2}
.vh-dot-current{background:#185FA5;box-shadow:0 0 0 3px #E6F1FB}
.vh-dot-published{background:#4CAF50}
.vh-card-inner{background:#fff;border:1px solid #E4E7EC;border-radius:10px;overflow:hidden}
.vh-card-current .vh-card-inner{box-shadow:0 0 0 2px #185FA5}
.vh-card-head{display:grid;grid-template-columns:36px 1fr auto;gap:14px;align-items:center;padding:12px 16px;cursor:pointer;user-select:none}
.vh-card-head:hover{background:#F7F8FA}
.vh-card-head:focus-visible{outline:2px solid #185FA5;outline-offset:-2px;border-radius:10px}
.vh-col-version{font-size:15px;font-weight:500;color:#1A2035;min-width:0;flex-shrink:0}
.vh-col-meta{min-width:0}
.vh-right{display:flex;align-items:center;gap:8px;min-width:110px;justify-content:flex-end}
.vh-badge{display:inline-flex;align-items:center;gap:3px;font-size:11px;font-weight:500;padding:2px 7px;border-radius:20px}
.vh-badge-current{background:#E6F1FB;color:#185FA5}
.vh-badge-published{background:#EAF3DE;color:#27500A}
.vh-meta-row1{display:flex;align-items:center;flex-wrap:wrap;gap:4px;margin-bottom:4px}
.vh-delta{font-size:12px;font-weight:500;padding:2px 7px;border-radius:6px}
.vh-delta-added{background:#EAF3DE;color:#27500A}
.vh-delta-updated{background:#FAEEDA;color:#633806}
.vh-delta-removed{background:#FCEBEB;color:#791F1F}
.vh-delta-none{font-size:12px;color:#90A4AE;font-style:italic}
.vh-meta-row,.vh-meta-row2{display:flex;align-items:center;gap:5px}
.vh-avatar{width:20px;height:20px;border-radius:50%;background:#E6F1FB;color:#185FA5;font-size:10px;font-weight:500;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.vh-avatar-sm{width:16px;height:16px;border-radius:50%;background:#E6F1FB;color:#0C447C;font-size:10px;font-weight:500;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.vh-meta-text{font-size:12px;color:#90A4AE}
.vh-restore-btn{background:transparent;border:1px solid #D0D5DD;border-radius:8px;padding:4px 10px;font-size:12px;color:#344054;cursor:pointer;white-space:nowrap}
.vh-restore-btn:hover{border-color:#185FA5;color:#185FA5}
.vh-chevron{font-size:16px;color:#90A4AE;transition:transform 200ms ease;flex-shrink:0}
.vh-card.is-open .vh-chevron{transform:rotate(180deg)}
.vh-expand-body{border-top-width:0;border-top-style:solid;border-top-color:#E4E7EC;padding:0 14px;background:#F7F8FA;overflow:hidden;max-height:0;transition:max-height .25s ease,padding .25s ease,border-top-width .25s}
.vh-expand-body-open{max-height:600px;padding:12px 14px;padding-bottom:12px;border-top-width:1px}
.vh-expand-body>p{margin:0}
.vh-diff-section{margin-bottom:10px}
.vh-diff-section:last-of-type{margin-bottom:0}
.vh-diff-label{font-size:11px;font-weight:500;text-transform:uppercase;letter-spacing:.06em;margin-bottom:5px;display:flex;align-items:center;gap:4px}
.vh-diff-label-added{color:#27500A}
.vh-diff-label-updated{color:#633806}
.vh-diff-label-removed{color:#791F1F}
.vh-diff-row{display:flex;align-items:center;gap:5px;font-size:13px;color:#1A2035;padding:2px 0}
.vh-diff-symbol{font-weight:500;font-size:13px;width:12px;flex-shrink:0}
.vh-diff-symbol-added{color:#27500A}
.vh-diff-symbol-updated{color:#633806}
.vh-diff-symbol-removed{color:#791F1F}
.vh-type-pill{font-size:11px;color:#90A4AE;background:#E4E7EC;border-radius:4px;padding:1px 5px;flex-shrink:0}
.vh-strikethrough{text-decoration:line-through;color:#607D8B}
.vh-arrow{color:#90A4AE;font-size:12px}
.vh-secondary-actions{display:flex;gap:8px;margin-top:12px;padding-top:12px;border-top:1px solid #E4E7EC}
.vh-action-btn{background:none;border:1px solid #E4E7EC;border-radius:8px;padding:4px 10px;font-size:12px;color:#607D8B;cursor:pointer;display:flex;align-items:center;gap:4px}
.vh-action-btn:hover{border-color:#185FA5;color:#185FA5}
.vh-overlay{position:absolute;inset:0;background:rgba(26,32,53,.45);display:flex;align-items:center;justify-content:center;z-index:100}
.vh-confirm-card{background:#fff;border-radius:10px;padding:24px;width:340px;box-shadow:0 4px 20px rgba(26,32,53,.18)}
.vh-confirm-title{font-size:15px;font-weight:500;color:#1A2035;margin:0 0 8px;display:flex;align-items:center;gap:6px}
.vh-confirm-desc{font-size:13px;color:#607D8B;line-height:1.6;margin:0 0 16px}
.vh-confirm-error{font-size:12px;color:#791F1F;background:#FCEBEB;padding:8px 10px;border-radius:6px;margin-bottom:12px;border:1px solid #F5C6C6}
.vh-confirm-actions{display:flex;gap:8px;justify-content:flex-end}
.vh-btn-ghost{background:#fff;border:1px solid #E4E7EC;border-radius:8px;padding:7px 16px;font-size:13px;color:#344054;cursor:pointer}
.vh-btn-ghost:hover:not(:disabled){border-color:#185FA5;color:#185FA5}
.vh-btn-primary{background:#185FA5;border:1px solid #185FA5;border-radius:8px;padding:7px 16px;font-size:13px;color:#fff;cursor:pointer;font-weight:500;display:flex;align-items:center;gap:4px}
.vh-btn-primary:hover:not(:disabled){background:#1050A0;border-color:#1050A0}
.vh-btn-primary:disabled,.vh-btn-ghost:disabled{opacity:.65;cursor:not-allowed}
.vh-body-state{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:320px;padding:24px 20px;text-align:center}
.vh-empty-icon-lg{font-size:40px;color:#C0CAD8;display:block;margin-bottom:12px}
.vh-empty-headline{font-size:15px;font-weight:500;color:#1A2035;margin:0 0 6px}
.vh-empty-desc{font-size:13px;color:#607D8B;line-height:1.55;max-width:320px;margin:0 0 16px;text-align:center}
.vh-toolbar-disabled{opacity:.5;pointer-events:none}
.vh-diff-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;margin-bottom:4px}
@media(max-width:720px){.vh-diff-grid{grid-template-columns:1fr}}
.vh-diff-col{min-width:0}
.vh-diff-empty-hint{font-size:13px;color:#C0CAD8;padding:2px 0;display:block}
.vh-expanded-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px}
.vh-group{min-width:0}
.vh-group-head{font-size:11px;font-weight:500;text-transform:uppercase;letter-spacing:.06em;margin-bottom:5px;display:flex;align-items:center;gap:4px}
.h-add{color:#27500A}
.h-upd{color:#633806}
.h-rem{color:#791F1F}
.vh-group-empty{font-size:13px;color:#90A4AE;padding:2px 0}
.vh-footer-bar{display:flex;align-items:flex-start;gap:8px;background:#F7F8FA;border-top:1px solid #E4E7EC;padding:10px 18px;font-size:12px;color:#607D8B;flex-shrink:0}
.vh-footer-icon{font-size:14px;color:#185FA5;flex-shrink:0;margin-top:1px}
.vh-action-btn-disabled{opacity:.5;cursor:not-allowed}
.vh-action-btn-disabled:hover{border-color:#E4E7EC !important;color:#607D8B !important}
.vh-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
`

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  formType: string
  versions: FormSchemaVersion[]
  loading: boolean
  error?: string
  currentUser: string
  onClose: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRestoreSuccess: (schema: { containersData: any[]; selectedLayout: any }) => void
}

const CHIP_LABELS: Record<FilterChip, string> = {
  all: 'All', current: 'Current', published: 'Published',
}

// ─── Component ────────────────────────────────────────────────────────────────

const VersionHistoryPanel: React.FC<Props> = React.memo(({
  formType, versions, loading, error, currentUser, onClose, onRestoreSuccess,
}) => {
  const [expanded,      setExpanded]      = useState<number | null>(null)
  const [restoreTarget, setRestoreTarget] = useState<number | null>(null)
  const [restoring,     setRestoring]     = useState(false)
  const [restoreError,  setRestoreError]  = useState<string | null>(null)
  const [liveMsg,       setLiveMsg]       = useState('')
  const [searchTerm,    setSearchTerm]    = useState('')
  const [filter,        setFilter]        = useState<FilterChip>('all')

  const autoExpandedRef = useRef(false)
  useEffect(() => {
    if (!autoExpandedRef.current && versions.length > 0) {
      const active = versions.find(v => v.IsActive)
      if (active) {
        setExpanded(active.Version)
        autoExpandedRef.current = true
      }
    }
  }, [versions])

  const allDiffs = useMemo<Map<number, DiffResult>>(() => {
    const map = new Map<number, DiffResult>()
    versions.forEach((v, idx) => map.set(v.Version, diffVersions(v, versions[idx + 1])))
    return map
  }, [versions])

  const filtered = useMemo<FormSchemaVersion[]>(() => {
    let list = versions
    if (filter !== 'all') list = list.filter(v => getStatus(v) === filter)
    const term = searchTerm.trim().toLowerCase()
    if (term) {
      list = list.filter(v => {
        if (`v${v.Version}`.includes(term)) return true
        if (v.ItemCreatedBy?.toLowerCase().includes(term)) return true
        const d = allDiffs.get(v.Version)
        if (!d) return false
        return [
          ...d.added.map(f => f.label),
          ...d.removed.map(f => f.label),
          ...d.updated.flatMap(u => [u.oldLabel, u.newLabel]),
        ].some(n => n.toLowerCase().includes(term))
      })
    }
    return list
  }, [versions, filter, searchTerm, allDiffs])

  const toggleExpand = useCallback((v: number) => {
    setExpanded(prev => prev === v ? null : v)
  }, [])

  const handleCardKey = useCallback((e: React.KeyboardEvent, v: number) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(v) }
  }, [toggleExpand])

  const openRestore = useCallback((v: number) => {
    setRestoreTarget(v); setRestoreError(null)
  }, [])

  const cancelRestore = useCallback(() => {
    setRestoreTarget(null); setRestoreError(null)
  }, [])

  const confirmRestore = useCallback(async () => {
    if (restoreTarget === null) return
    setRestoring(true)
    setRestoreError(null)
    try {
      const ok = await rollbackSchema(formType, restoreTarget, currentUser)
      if (!ok) throw new Error('Restore failed — please try again.')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const schema = await loadActiveSchema(formType) as { containersData: any[]; selectedLayout: any } | null
      if (!schema) throw new Error('Schema could not be read after restore.')
      setLiveMsg(`Version v${restoreTarget} restored successfully.`)
      setExpanded(restoreTarget)
      onRestoreSuccess(schema)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'An unexpected error occurred.'
      setRestoreError(msg)
      setLiveMsg(`Restore failed: ${msg}`)
    } finally {
      setRestoring(false)
    }
  }, [restoreTarget, formType, onRestoreSuccess])

  return (
    <>
      <style>{VH_STYLES}</style>
      <div className="vh-panel">

        {/* ── Sticky header ── */}
        <div className="vh-header">
          <div className="vh-title-row">
            <h2 className="vh-title">Version history</h2>
            <span className="vh-count">
              {loading ? 'Loading…' : `${versions.length} version${versions.length === 1 ? '' : 's'}`}
            </span>
            <button className="vh-close" onClick={onClose} aria-label="Close">
              <i className="icon-rb-close" aria-hidden="true" />
            </button>
          </div>

          <div className={`vh-toolbar${loading || !!error || versions.length === 0 ? ' vh-toolbar-disabled' : ''}`}>
            <div className="vh-search">
              <i className="icon-rb-search" aria-hidden="true" />
              <input
                type="search"
                placeholder="Search version, user, or field…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                aria-label="Search versions"
              />
            </div>
            <div className="vh-filters" role="group" aria-label="Filter versions">
              {(Object.keys(CHIP_LABELS) as FilterChip[]).map(chip => (
                <button
                  key={chip}
                  className={`vh-chip${filter === chip ? ' is-active' : ''}`}
                  onClick={() => setFilter(chip)}
                  aria-pressed={filter === chip}
                >
                  {CHIP_LABELS[chip]}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Timeline ── */}
        <div className="vh-timeline">
          {loading ? (
            <div className="vh-body-state">
              <i className="icon-rb-clock vh-empty-icon-lg" aria-hidden="true" />
              <p className="vh-empty-headline" style={{color:'#607D8B'}}>Loading version history…</p>
            </div>
          ) : error ? (
            <div className="vh-body-state">
              <i className="icon-rb-close vh-empty-icon-lg" style={{color:'#791F1F'}} aria-hidden="true" />
              <p className="vh-empty-headline" style={{color:'#791F1F'}}>Could not load version history.</p>
              <p className="vh-empty-desc">The FormSchemaHistory list may not be provisioned on the server yet. Check the browser console for details.</p>
            </div>
          ) : versions.length === 0 ? (
            <div className="vh-body-state">
              <i className="icon-rb-history vh-empty-icon-lg" aria-hidden="true" />
              <p className="vh-empty-headline">No version history yet</p>
              <p className="vh-empty-desc">Publish this form to create your first version. Every publish saves a snapshot you can restore from.</p>
              <button className="vh-btn-ghost" style={{fontSize:13}} onClick={onClose}>Close</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="vh-body-state" style={{minHeight:160}}>
              <span className="vh-meta-text" style={{fontSize:13}}>
                {searchTerm.trim() && filter !== 'all'
                  ? `No ${filter} versions match '${searchTerm.trim()}'`
                  : searchTerm.trim()
                  ? `No versions match '${searchTerm.trim()}'`
                  : `No ${filter} versions yet`}
              </span>
            </div>
          ) : (
            <div className="vh-rail">
              {filtered.map(v => {
                const status     = getStatus(v)
                const diff       = allDiffs.get(v.Version) ?? { added: [], removed: [], updated: [] }
                const isExpanded = expanded === v.Version
                const hasDiff    = diff.added.length + diff.removed.length + diff.updated.length > 0
                const date       = v.ItemCreated ? formatDate(v.ItemCreated) : null

                return (
                  <div key={v.Version} className={`vh-card${status === 'current' ? ' vh-card-current' : ''}${isExpanded ? ' is-open' : ''}`}>
                    <div className={`vh-dot vh-dot-${status}`} aria-hidden="true" />
                    <div className="vh-card-inner">

                      {/* Card header */}
                      <div
                        className="vh-card-head"
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        aria-label={`Version ${v.Version}, ${status}. ${isExpanded ? 'Collapse' : 'Expand'} details.`}
                        onClick={() => toggleExpand(v.Version)}
                        onKeyDown={e => handleCardKey(e, v.Version)}
                      >
                        <span className="vh-col-version">v{v.Version}</span>

                        <div className="vh-col-meta">
                          <div className="vh-meta-row1">
                            <span className={`vh-badge vh-badge-${status}`}>
                              {status === 'current'   && <i className="icon-rb-checked"      aria-hidden="true" />}
                              {status === 'published' && <i className="icon-rb-file-up-arrow" aria-hidden="true" />}
                              {' '}{status.charAt(0).toUpperCase() + status.slice(1)}
                            </span>
                            {!hasDiff && <span className="vh-delta-none">No field changes</span>}
                            {diff.added.length   > 0 && <span className="vh-delta vh-delta-added">+{diff.added.length} added</span>}
                            {diff.updated.length > 0 && <span className="vh-delta vh-delta-updated">~{diff.updated.length} updated</span>}
                            {diff.removed.length > 0 && <span className="vh-delta vh-delta-removed">-{diff.removed.length} removed</span>}
                          </div>
                          {date && (
                            <div className="vh-meta-row2">
                              <span className="vh-meta-text">{date}</span>
                              {v.ItemCreatedBy?.trim() && (
                                <>
                                  <span className="vh-meta-text">·</span>
                                  <div className="vh-avatar-sm" aria-hidden="true">{getInitials(v.ItemCreatedBy.trim())}</div>
                                  <span className="vh-meta-text">{v.ItemCreatedBy.trim()}</span>
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="vh-right">
                          {v.IsActive
                            ? <div style={{width:70}} aria-hidden="true" />
                            : (
                              <button
                                className="vh-restore-btn"
                                onClick={e => { e.stopPropagation(); openRestore(v.Version) }}
                                aria-label={`Restore version ${v.Version}`}
                              >
                                Restore
                              </button>
                            )
                          }
                          <i className="icon-rb-arrow-down vh-chevron" aria-hidden="true" />
                        </div>
                      </div>

                      {/* Expand body */}
                      <div className={`vh-expand-body${isExpanded ? ' vh-expand-body-open' : ''}`} aria-hidden={!isExpanded}>
                        <div className="vh-expanded-grid">
                          <div className="vh-group">
                            <div className="vh-group-head h-add">Added ({diff.added.length})</div>
                            {diff.added.length > 0 ? diff.added.map(f => (
                              <div key={f.key} className="vh-diff-row">
                                <span className="vh-diff-symbol vh-diff-symbol-added">+</span>
                                <span>{f.label}</span>
                                <span className="vh-type-pill">{f.type}</span>
                              </div>
                            )) : <div className="vh-group-empty">—</div>}
                          </div>
                          <div className="vh-group">
                            <div className="vh-group-head h-upd">Updated ({diff.updated.length})</div>
                            {diff.updated.length > 0 ? diff.updated.map(f => (
                              <div key={f.key} className="vh-diff-row">
                                <span className="vh-diff-symbol vh-diff-symbol-updated">~</span>
                                <span className="vh-strikethrough">{f.oldLabel}</span>
                                <span className="vh-arrow">→</span>
                                <span>{f.newLabel}</span>
                                <span className="vh-type-pill">{f.type}</span>
                              </div>
                            )) : <div className="vh-group-empty">—</div>}
                          </div>
                          <div className="vh-group">
                            <div className="vh-group-head h-rem">Removed ({diff.removed.length})</div>
                            {diff.removed.length > 0 ? diff.removed.map(f => (
                              <div key={f.key} className="vh-diff-row">
                                <span className="vh-diff-symbol vh-diff-symbol-removed">−</span>
                                <span>{f.label}</span>
                                <span className="vh-type-pill">{f.type}</span>
                              </div>
                            )) : <div className="vh-group-empty">—</div>}
                          </div>
                        </div>

                        {!v.IsActive && (
                          <div className="vh-secondary-actions">
                            <button
                              className="vh-action-btn vh-action-btn-disabled"
                              disabled
                              aria-disabled="true"
                              title="Coming soon"
                              aria-label="Compare with current version (coming soon)"
                            >
                              <i className="icon-rb-arrow-exchange font-12" aria-hidden="true" /> Compare with current
                            </button>
                            <button
                              className="vh-action-btn vh-action-btn-disabled"
                              disabled
                              aria-disabled="true"
                              title="Coming soon"
                              aria-label="Preview this version (coming soon)"
                            >
                              <i className="icon-rb-eye font-12" aria-hidden="true" /> Preview this version
                            </button>
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* ── Footer info bar ── */}
        <div className="vh-footer-bar">
          <i className="icon-rb-info-circle vh-footer-icon" aria-hidden="true" />
          <span>Restoring a version will replace the current form configuration with the selected version. A new version is created — no history is lost.</span>
        </div>

        {/* ── Restore confirmation overlay ── */}
        {restoreTarget !== null && (
          <div className="vh-overlay" role="dialog" aria-modal="true" aria-labelledby="vh-confirm-title">
            <div className="vh-confirm-card">
              <p className="vh-confirm-title" id="vh-confirm-title">
                <i className="icon-rb-bookmark" aria-hidden="true" />
                Restore v{restoreTarget} as current version?
              </p>
              <p className="vh-confirm-desc">
                v{restoreTarget} will become the active version. The current version will be deactivated but remains in history.
              </p>
              {restoreError && (
                <div className="vh-confirm-error" role="alert">
                  <i className="icon-rb-close me-1" aria-hidden="true" />
                  {restoreError}
                </div>
              )}
              <div className="vh-confirm-actions">
                <button className="vh-btn-ghost" onClick={cancelRestore} disabled={restoring}>
                  Cancel
                </button>
                <button className="vh-btn-primary" onClick={confirmRestore} disabled={restoring}>
                  {restoring
                    ? <><i className="icon-rb-clock" aria-hidden="true" /> Restoring…</>
                    : <><i className="icon-rb-checked" aria-hidden="true" /> Restore version</>
                  }
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Accessible live region ── */}
        <div role="status" aria-live="polite" className="vh-sr-only">{liveMsg}</div>

      </div>
    </>
  )
})

VersionHistoryPanel.displayName = 'VersionHistoryPanel'

export default VersionHistoryPanel
